import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { useUsers } from '@/services/users';
import { getUserDisplayName } from '@/utils/user-display';
import { darkBorder, darkText, darkTextDim } from '@/shared/dark-inline-styles';

interface Props {
    /** The user ids currently on the guest list. */
    value: string[];
    onChange: (userIds: string[]) => void;
}

/**
 * Who may enter a PRIVATE live room.
 *
 * Not a nicety — for `privacy: 'private'` the API's `canJoinLiveSession` reads
 * `accessUserIds` and nothing else, and it fails closed. This modal offered
 * "Private — invited members only" while never sending a list, so every private
 * session an admin created was a room only its host could enter. Nothing errored;
 * it simply did not work, which is why it went unnoticed.
 *
 * SEARCH-then-pick rather than a dropdown of everybody: the user table is the
 * whole member base, and a select with thousands of options is not a control. The
 * chosen guests stay pinned above the results so they cannot be lost behind the
 * next search — the list being built is the thing the admin is reasoning about,
 * not the query.
 *
 * The names of already-chosen guests come from whichever search surfaced them, so
 * an id restored from a saved session shows as the raw id until it is searched
 * for. That is deliberate rather than a lookup-per-id: it is honest about what we
 * know, and it costs nothing when editing a session you just created.
 */
const GuestPicker = ({ value, onChange }: Props) => {
    const [query, setQuery] = useState('');
    const [names, setNames] = useState<Record<string, string>>({});

    // Only search once there is something to search FOR. An empty query would
    // pull the first page of every member, which is a list nobody wants and a
    // request nobody asked for.
    const trimmed = query.trim();
    const { data, isFetching } = useUsers(1, 10, trimmed.length >= 2 ? { search: trimmed } : {});
    const results = useMemo(() => (trimmed.length >= 2 ? (data?.data?.users ?? []) : []), [data, trimmed]);

    const add = (id: string, name: string) => {
        setNames((prev) => ({ ...prev, [id]: name }));
        if (!value.includes(id)) onChange([...value, id]);
    };

    return (
        <Wrap>
            <Label>Invited members</Label>
            <Hint>
                A private session has no other door — only the people on this list can get in, and sharing the link is not enough on its own. The host
                can invite more from inside the room.
            </Hint>

            {value.length > 0 && (
                <Chips>
                    {value.map((id) => (
                        <Chip key={id}>
                            <span>{names[id] ?? id}</span>
                            <Remove type="button" aria-label="Remove" onClick={() => onChange(value.filter((item) => item !== id))}>
                                ✕
                            </Remove>
                        </Chip>
                    ))}
                </Chips>
            )}

            <Search
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search members by name or email"
                aria-label="Search members"
            />

            {trimmed.length >= 2 && (
                <Results>
                    {isFetching && <Muted>Searching…</Muted>}
                    {!isFetching && results.length === 0 && <Muted>Nobody matches “{trimmed}”.</Muted>}
                    {results.map((user) => {
                        const name = getUserDisplayName(user);
                        const already = value.includes(user.id);
                        return (
                            <Result key={user.id} type="button" disabled={already} onClick={() => add(user.id, name)}>
                                <strong>{name}</strong>
                                <small>{user.email}</small>
                                {already && <Added>Added</Added>}
                            </Result>
                        );
                    })}
                </Results>
            )}
        </Wrap>
    );
};

const Wrap = styled.div`
    margin-bottom: 16px;
`;

const Label = styled.p`
    color: ${darkText};
    font-size: 13px;
    font-weight: 600;
    margin: 0 0 2px;
`;

const Hint = styled.p`
    color: ${darkTextDim};
    font-size: 12px;
    line-height: 1.5;
    margin: 0 0 10px;
`;

const Chips = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 8px;
`;

const Chip = styled.span`
    align-items: center;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid ${darkBorder};
    border-radius: 999px;
    color: ${darkText};
    display: inline-flex;
    font-size: 12px;
    gap: 6px;
    padding: 3px 6px 3px 10px;
`;

const Remove = styled.button`
    background: transparent;
    border: none;
    color: ${darkTextDim};
    cursor: pointer;
    font-size: 11px;
    line-height: 1;
    padding: 2px 4px;

    &:hover {
        color: ${darkText};
    }
`;

const Search = styled.input`
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid ${darkBorder};
    border-radius: 4px;
    color: ${darkText};
    font-size: 13px;
    padding: 8px 10px;
    width: 100%;

    &::placeholder {
        color: ${darkTextDim};
    }
`;

const Results = styled.div`
    border: 1px solid ${darkBorder};
    border-radius: 4px;
    margin-top: 6px;
    max-height: 190px;
    overflow-y: auto;
`;

const Result = styled.button`
    background: transparent;
    border: none;
    border-bottom: 1px solid ${darkBorder};
    color: ${darkText};
    cursor: pointer;
    display: flex;
    align-items: baseline;
    gap: 8px;
    padding: 8px 10px;
    text-align: left;
    width: 100%;

    &:last-child {
        border-bottom: none;
    }
    &:hover:not(:disabled) {
        background: rgba(255, 255, 255, 0.06);
    }
    &:disabled {
        cursor: default;
        opacity: 0.55;
    }

    small {
        color: ${darkTextDim};
        font-size: 11px;
    }
`;

const Added = styled.span`
    color: ${darkTextDim};
    font-size: 11px;
    margin-left: auto;
`;

const Muted = styled.p`
    color: ${darkTextDim};
    font-size: 12px;
    margin: 0;
    padding: 10px;
`;

export default GuestPicker;
